import { getIndexPageUrl, getStockPageUrl } from "@/app/utils/stock-url";
import { DirectPaths } from "@/config/routes";

export const NAV_SEARCH_CATEGORIES = [
    { id: "all", label: "All" },
    { id: "broking", label: "Incred Money Broking" },
    { id: "indices", label: "Indices" },
    { id: "etfs", label: "ETFs" },
    { id: "gold-silver", label: "Digital Gold & Silver" },
    { id: "global-stocks", label: "Global Stocks" },
    { id: "fixed-deposits", label: "Fixed Deposits" },
] as const;

export type NavSearchCategory = (typeof NAV_SEARCH_CATEGORIES)[number]["id"];

export type NavSearchItem = {
    label: string;
    href: string;
    category: Exclude<NavSearchCategory, "all">;
    openInNewTab?: boolean;
};

const PROD_BASE = (process.env.ICM_PROD_URL || "https://www.incredmoney.com").replace(/\/$/, "");

const stock = (label: string, href?: string): NavSearchItem => ({
    label,
    href: href ?? getStockPageUrl(label),
    category: "broking",
});

const indexItem = (label: string): NavSearchItem => ({
    label,
    href: getIndexPageUrl(label),
    category: "indices",
});

const etf = (label: string, slug: string): NavSearchItem => ({
    label,
    href: `${PROD_BASE}${DirectPaths.ETF}/${slug}`,
    category: "etfs",
    openInNewTab: true,
});

const globalStock = (label: string, path: string): NavSearchItem => ({
    label,
    href: `${PROD_BASE}/global/us-stocks/${path}`,
    category: "global-stocks",
});

const fd = (label: string, slug: string): NavSearchItem => ({
    label,
    href: `${DirectPaths.FD_POSTFIX_PATH}/${slug}`,
    category: "fixed-deposits",
});

/** Shared product rows reused across trending lists and the searchable catalog. */
const RELIANCE = stock("Reliance Industries Ltd");
const HDFC_BANK = stock("HDFC Bank Ltd");
const TCS = stock("TCS", `${PROD_BASE}${DirectPaths.STOCKS}/tata-consultancy-services-ltd`);
const INFOSYS = stock("Infosys Limited", `${PROD_BASE}${DirectPaths.STOCKS}/infosys-ltd`);
const NIFTY_50 = indexItem("Nifty 50");
const NIFTY_BANK = indexItem("Nifty Bank");
const NIFTY_500 = indexItem("Nifty 500");
const NIFTY_IT = indexItem("Nifty IT");
const GOLD_ETFS = etf("Gold ETFs", "gold-etfs");
const SILVER_ETFS = etf("Silver ETFs", "silver-etfs");
const DEBT_ETFS = etf("Debt ETFs", "debt-etfs");
const EQUITY_ETFS = etf("Equity ETFs", "equity-etfs");
const GOLD = {
    label: "Gold",
    href: `${DirectPaths.BULLION_POSTFIX_PATH}${DirectPaths.GOLD}`,
    category: "gold-silver" as const,
};
const SILVER = {
    label: "Silver",
    href: `${DirectPaths.BULLION_POSTFIX_PATH}${DirectPaths.SILVER}`,
    category: "gold-silver" as const,
};
const NVIDIA = globalStock("NVIDIA", "NVDA/nvidia-corp");
const TESLA = globalStock("Tesla", "TSLA/tesla-inc");
const APPLE = globalStock("Apple", "AAPL/apple-inc");
const AMAZON = globalStock("Amazon", "AMZN/amazon.com-inc");
const SLICE_FD = fd("Slice Small Finance Bank", "slice-small-finance-bank");
const SURYODAY_FD = fd("Suryoday Small Finance Bank", "suryoday-small-finance-bank");
const UTKARSH_FD = fd("Utkarsh Small Finance Bank", "utkarsh-small-finance-bank");
const DCB_FD = fd("DCB Bank Ltd.", "dcb-bank-ltd");

/**
 * Trending rows shown when the search query is empty, keyed by selected category.
 */
export const NAV_SEARCH_TRENDING: Record<NavSearchCategory, readonly NavSearchItem[]> = {
    all: [RELIANCE, HDFC_BANK, NIFTY_50, GOLD_ETFS],
    broking: [RELIANCE, HDFC_BANK, TCS, INFOSYS],
    indices: [NIFTY_50, NIFTY_BANK, NIFTY_500, NIFTY_IT],
    etfs: [GOLD_ETFS, SILVER_ETFS, DEBT_ETFS, EQUITY_ETFS],
    "gold-silver": [GOLD, SILVER],
    "global-stocks": [NVIDIA, TESLA, APPLE, AMAZON],
    "fixed-deposits": [SLICE_FD, SURYODAY_FD, UTKARSH_FD, DCB_FD],
};

/**
 * Searchable catalog. Includes trending destinations plus category hubs and
 * additional equities used by the header/footer elsewhere.
 */
export const NAV_SEARCH_ITEMS: readonly NavSearchItem[] = [
    RELIANCE,
    HDFC_BANK,
    TCS,
    INFOSYS,
    stock("Tata Power Company Ltd"),
    stock("State Bank of India"),
    stock("Adani Power Ltd"),
    stock("ITC Ltd"),
    stock("Wipro Ltd"),
    stock("Yes Bank Ltd"),
    stock("Hindustan Aeronautics Ltd"),
    {
        label: "Incred Money Broking",
        href: DirectPaths.STOCKS,
        category: "broking",
    },
    {
        label: "Stocks",
        href: DirectPaths.STOCKS,
        category: "broking",
    },
    NIFTY_50,
    NIFTY_BANK,
    NIFTY_500,
    NIFTY_IT,
    indexItem("BSE Sensex"),
    {
        label: "Indices",
        href: `${PROD_BASE}/indices`,
        category: "indices",
        openInNewTab: true,
    },
    GOLD_ETFS,
    SILVER_ETFS,
    DEBT_ETFS,
    EQUITY_ETFS,
    {
        label: "ETFs",
        href: `${PROD_BASE}${DirectPaths.ETF}`,
        category: "etfs",
        openInNewTab: true,
    },
    GOLD,
    SILVER,
    {
        label: "Digital Gold & Silver",
        href: DirectPaths.BULLION_POSTFIX_PATH,
        category: "gold-silver",
    },
    NVIDIA,
    TESLA,
    APPLE,
    AMAZON,
    {
        label: "Global Stocks",
        href: DirectPaths.GLOBAL + DirectPaths.US_STOCKS,
        category: "global-stocks",
    },
    {
        label: "Global ETFs",
        href: DirectPaths.GLOBAL + DirectPaths.US_ETFS,
        category: "global-stocks",
    },
    SLICE_FD,
    SURYODAY_FD,
    UTKARSH_FD,
    DCB_FD,
    {
        label: "Fixed Deposits",
        href: DirectPaths.FD_POSTFIX_PATH,
        category: "fixed-deposits",
    },
];

export function getNavSearchResults(
    category: NavSearchCategory,
    query: string,
): NavSearchItem[] {
    const normalized = query.trim().toLowerCase();
    const inCategory = (item: NavSearchItem) =>
        category === "all" || item.category === category;

    if (!normalized) {
        return [...NAV_SEARCH_TRENDING[category]];
    }

    return NAV_SEARCH_ITEMS.filter(
        (item) => inCategory(item) && item.label.toLowerCase().includes(normalized),
    );
}
